import type { SalesFunnelFilterDto } from '@app/api/SalesFunnels';

export interface LeadsSalesFunnelOption {
  value: number;
  label: string;
}

export interface LeadsSalesFunnelSelection {
  salesFunnelId: number | null;
  funnelStages: number[];
}

// Explicit "no funnel" choice (the user unchecked every stage in the funnels
// filter). Persisted as a sentinel id so it stays distinguishable from the
// `null + []` default marker, which resolves to the first funnel.
export const LEADS_SALES_FUNNEL_CLEARED_ID = 0;

// Query param that opens the funnels board on a given funnel (the funnels
// registry links to the board with it).
export const LEADS_SALES_FUNNEL_ID_SEARCH_PARAM = 'salesFunnelId';

export const LEADS_SALES_FUNNEL_CLEARED_SELECTION: LeadsSalesFunnelSelection = {
  salesFunnelId: LEADS_SALES_FUNNEL_CLEARED_ID,
  funnelStages: [],
};

export const isLeadsSalesFunnelSelectionCleared = (
  selection: LeadsSalesFunnelSelection
): boolean => selection.salesFunnelId === LEADS_SALES_FUNNEL_CLEARED_ID;

export const getSalesFunnelDisplayName = (
  funnel: SalesFunnelFilterDto
): string => funnel.Name ?? `Воронка ${funnel.Id}`;

export const mapSalesFunnelsToOptions = (
  funnels: SalesFunnelFilterDto[]
): LeadsSalesFunnelOption[] =>
  funnels.map((funnel) => ({
    value: funnel.Id,
    label: getSalesFunnelDisplayName(funnel),
  }));

export const getSalesFunnelStageIds = (
  funnels: SalesFunnelFilterDto[],
  funnelId: number | null
): number[] => {
  if (funnelId === null) return [];

  const selectedFunnel = funnels.find((funnel) => funnel.Id === funnelId);

  return (selectedFunnel?.Stages ?? []).map((stage) => stage.Id);
};

const hasSameStageIds = (firstStageIds: number[], secondStageIds: number[]) => {
  if (firstStageIds.length !== secondStageIds.length) {
    return false;
  }

  const secondStageIdSet = new Set(secondStageIds);

  return firstStageIds.every((stageId) => secondStageIdSet.has(stageId));
};

// `null + []` is the persisted condition "use the first available funnel",
// not a concrete empty selection. Resolving it at runtime keeps the default
// dynamic when the funnel list or its ordering changes. The cleared marker is
// the one persisted state that must NOT fall back to the first funnel: it
// resolves to "no funnel" so the page shows the funnel selector instead.
export const resolveLeadsSalesFunnelSelection = (
  funnels: SalesFunnelFilterDto[],
  selection: LeadsSalesFunnelSelection
): LeadsSalesFunnelSelection => {
  if (isLeadsSalesFunnelSelectionCleared(selection)) {
    return { salesFunnelId: null, funnelStages: [] };
  }

  const selectedFunnel = funnels.find(
    (funnel) => funnel.Id === selection.salesFunnelId
  );
  const effectiveFunnel = selectedFunnel ?? funnels[0];

  if (!effectiveFunnel) {
    return selection;
  }

  return {
    salesFunnelId: effectiveFunnel.Id,
    funnelStages:
      selectedFunnel && selection.funnelStages.length > 0
        ? selection.funnelStages
        : getSalesFunnelStageIds(funnels, effectiveFunnel.Id),
  };
};

// A complete selection of the current first funnel is equivalent to the
// semantic default and must not freeze its id/stages in localStorage.
export const normalizeLeadsSalesFunnelSelection = (
  funnels: SalesFunnelFilterDto[],
  selection: LeadsSalesFunnelSelection
): LeadsSalesFunnelSelection => {
  if (isLeadsSalesFunnelSelectionCleared(selection)) {
    return selection.funnelStages.length === 0
      ? selection
      : LEADS_SALES_FUNNEL_CLEARED_SELECTION;
  }

  const defaultFunnelId = funnels[0]?.Id ?? null;

  if (defaultFunnelId === null) {
    return selection;
  }

  const selectedFunnelExists = funnels.some(
    (funnel) => funnel.Id === selection.salesFunnelId
  );

  if (selection.salesFunnelId === null || !selectedFunnelExists) {
    return selection.salesFunnelId === null &&
      selection.funnelStages.length === 0
      ? selection
      : {
          salesFunnelId: null,
          funnelStages: [],
        };
  }

  if (selection.salesFunnelId !== defaultFunnelId) {
    return selection;
  }

  const defaultStageIds = getSalesFunnelStageIds(funnels, defaultFunnelId);
  const selectedStageIds =
    selection.funnelStages.length > 0
      ? selection.funnelStages
      : defaultStageIds;

  if (!hasSameStageIds(defaultStageIds, selectedStageIds)) {
    return selection;
  }

  return {
    salesFunnelId: null,
    funnelStages: [],
  };
};
